import { Divider, Table, Spin, Tooltip, Row, Col, Modal, Button } from 'antd'
import { useEffect, useState } from 'react'
import { getSocialAbuse } from 'src/api/social'
import { downloadAttachment } from 'src/api/image'
import { DownloadOutlined } from '@ant-design/icons'

import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { isoToTimestamp } from 'src/utils/timestamp'
import UserSearch from 'src/components/Search/UserSearch'

const AbuseReason = () => {
  const [loading, setLoading] = useState(true)
  const [username, setUsername] = useState(undefined)
  const [abuserUsername, setAbuserUsername] = useState(undefined)
  const [transformedData, setTransformedData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [previewImage, setPreviewImage] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const nonPreviewableTypes = ['pdf', 'psd', 'heic']

  const handleImageClick = (contentObj, fileType) => {
    if (nonPreviewableTypes.includes(fileType)) {
      downloadFile(contentObj)
      return
    } else {
      setPreviewImage(contentObj.blobUrl)
      setPreviewVisible(true)
    }
  }

  const downloadFile = async (contentObj) => {
    const a = document.createElement('a')
    a.href = contentObj.blobUrl
    a.download = 'attachment.' + contentObj.fileType
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    URL.revokeObjectURL(contentObj.blobUrl)
  }

  const transformData = async (data) => {
    if (data == null) {
      return null
    }

    return await Promise.all(
      data.map(async (item) => {
        const resourceContents = await downloadAttachment(
          item.attachment?.attachmentId,
          item.attachment?.fileType
        )

        return {
          ...item,
          username: item.user?.username,
          abuserUsername: item.abuser?.username,
          reportReasonTitle: item.reportReason?.title,
          reportReasonDesc: item.reportReason?.description,
          description: item.description,
          resourceContents: resourceContents,
          date: isoToTimestamp(item.createdOn),
        }
      })
    )
  }

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination
    setCurrentPage(pagination.current)
    setPageSize(pagination.pageSize)
  }

  const fetchBadContentData = async (page, size, userId, abuserId) => {
    try {
      setLoading(true)
      const response = await getSocialAbuse(page - 1, size, userId, abuserId)
      const transformedData = await transformData(response.data)
      setTransformedData(transformedData)
      setTotalRecords(response.total_items)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBadContentData(
      currentPage,
      pageSize,
      username || '',
      abuserUsername || ''
    )
  }, [pageSize, currentPage, username, abuserUsername])

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'username',
      key: 'username',
      width: 200,
    },
    {
      title: 'Abuser User Name',
      dataIndex: 'abuserUsername',
      key: 'abuserUsername',
      width: 200,
    },
    {
      title: 'Report Reason Title',
      dataIndex: 'reportReasonTitle',
      key: 'reportReasonTitle',
      width: 300,
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 20 ? text.substring(0, 20) + '...' : text}
        </Tooltip>
      ),
    },
    {
      title: 'Report Reason Description',
      dataIndex: 'reportReasonDesc',
      key: 'reportReasonDesc',
      width: 400,
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 20 ? text.substring(0, 20) + '...' : text}
        </Tooltip>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 20 ? text.substring(0, 20) + '...' : text}
        </Tooltip>
      ),
    },
    {
      title: 'Attachments',
      dataIndex: 'resourceName',
      key: 'resourceName',
      render: (_, record) => {
        if (record.resourceContents && record.resourceContents.length > 0) {
          return (
            <Row gutter={[16, 16]} justify="start">
              {record.resourceContents.map((contentObj, index) => {
                const fileType = contentObj.fileType
                return (
                  <Col key={index} span={6}>
                    {nonPreviewableTypes.includes(fileType) ? (
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => handleImageClick(contentObj, fileType)}
                      >
                        {fileType.toUpperCase()}
                      </Button>
                    ) : (
                      <img
                        src={contentObj.blobUrl}
                        alt="Attachment"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '1px solid #ddd',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                        }}
                        onClick={() => handleImageClick(contentObj, fileType)}
                      />
                    )}
                  </Col>
                )
              })}
            </Row>
          )
        }
        return null
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 350,
    },
  ]

  return (
    <>
      <Spin spinning={loading}>
        <UserSearch
          placeholder="Search Username"
          value={username}
          onChange={(value) => {
            setUsername(value)
            setCurrentPage(1)
            setPageSize(10)
            fetchBadContentData(1, 10, value, abuserUsername)
          }}
        />

        <UserSearch
          placeholder="Search Abuser Username"
          value={abuserUsername}
          onChange={(value) => {
            setAbuserUsername(value)
            setCurrentPage(1)
            setPageSize(10)
            fetchBadContentData(1, 10, username, value)
          }}
        />
        <Divider />
        <DndProvider backend={HTML5Backend}>
          <Table
            dataSource={transformedData}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              total: totalRecords,
            }}
            onChange={handleTableChange}
          />
        </DndProvider>
      </Spin>
      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="40%"
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
        bodyStyle={{
          padding: 0,
          textAlign: 'center',
        }}
      >
        <img
          alt="Preview"
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '70vh',
            objectFit: 'contain',
            borderRadius: '8px',
          }}
          src={previewImage}
        />
      </Modal>
    </>
  )
}

export default AbuseReason
